import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import * as Icon from 'react-feather';
import BreadCrumbs from '../../layouts/breadcrumbs/BreadCrumbs';
import ComponentCard from '../ComponentCard';
import useHttp from '../../hooks/useHttp';
import useAlert from '../../hooks/useAlert';
import customStyles from '../TableStyles';
import FilterComponent from '../utils/Filter';
import Loader from '../../layouts/loader/Loader';
import { withPagePermission, PERMISSIONS } from '../permission';

const PartnerUserList = () => {
  const [users, setUsers] = useState([]);
  const [filterText, setFilterText] = useState('');
  const isDarkMode = useSelector((state) => state.customizer.isDark);
  const { isLoading, sendRequest } = useHttp();
  const { alert } = useAlert();
  const navigate = useNavigate();
  const roleLabels = {
    'group-caf-only': 'CAF Only',
    'group-renewal-topup': 'Renewal And Topup',
    'group-full-access': 'Full Access',
  };

  useEffect(() => {
    sendRequest(
      {
        method: 'GET',
        url: `${process.env.REACT_APP_IBILL_GATEWAY}/hub/api/v1/partner-user/users`,
        headers: { Accept: 'application/json' },
      },
      (data) => setUsers(data || [])
    );
  }, [sendRequest]);

  const filteredUsers = users.filter(
    (user) =>
      (user.username && user.username.toLowerCase().includes(filterText.toLowerCase())) ||
      (user.name && user.name.toLowerCase().includes(filterText.toLowerCase())) ||
      (user.email && user.email.toLowerCase().includes(filterText.toLowerCase())) ||
      (user.phone && user.phone.toLowerCase().includes(filterText.toLowerCase()))
  );

  const columns = useMemo(
    () => [
      {
        name: 'Username',
        selector: (row) => row.username,
        sortable: true,
        width: '200px',
      },
      {
        name: 'Name',
        selector: (row) => row.name,
        sortable: true,
        width: '250px',
      },
      {
        name: 'Role',
        selector: (row) => roleLabels[row.role] || row.role,
        sortable: true,
        width: '150px',
      },
      {
        name: 'Phone',
        selector: (row) => row.phone,
        sortable: true,
        width: '150px',
      },
      {
        name: 'Email',
        selector: (row) => row.email,
        sortable: true,
        width: '250px',
      },
      {
        name: 'Actions',
        button: true,
        width: '75px',
        cell: (row) => {
          return (
            <>
              <Link className="btn btn-warning btn-sm m-1" to={`/partner/user/edit/${row.username}`}><Icon.Edit size={20}/></Link>
            </>
          );
        }
      },
      {
        name: 'Zone Accesses',
        width: '300px',
        wrap: true,
        selector: (row) =>
          row['zone-accesses'] && row['zone-accesses'].length > 0 ? (
            <ul>
              {row['zone-accesses'].map((zone, index) => (
                <li
                  key={zone.id}
                  style={index === 0 ? { border: '.5px solid yellow', padding: '5px' } : {}}
                >
                  {zone['zone-name']} ({zone['zone-code']})
                </li>
              ))}
            </ul>
          ) : (
            'N/A'
          ),
        sortable: false,
      },
      {
        name: 'Status',
        selector: (row) => row.status,
        sortable: true,
        width: '150px',
      },
      {
        name: 'Created On',
        selector: (row) => row['created-on'],
        sortable: true,
        width: '200px',
      },
      {
        name: 'Updated On',
        selector: (row) => row['updated-on'],
        sortable: true,
        width: '200px',
      },
    ],
    [navigate]
  );

  const subHeaderComponentMemo = useMemo(() => {
    return (
      <FilterComponent onFilter={(e) => setFilterText(e.target.value)} filterText={filterText} />
    );
  }, [filterText]);

  return (
    <div>
      {alert}
      <BreadCrumbs />
      <ComponentCard title="Partner Users" buttonLabel="Create User" buttonLink="/partner/user/create">
        {isLoading ? (
          <Loader />
        ) : (
          <DataTable
            columns={columns}
            data={filteredUsers}
            striped
            dense
            theme={isDarkMode ? 'iDark' : 'iLight'}
            customStyles={customStyles}
            subHeader
            subHeaderComponent={subHeaderComponentMemo}
            fixedHeader
            fixedHeaderScrollHeight="500px"
          />
        )}
      </ComponentCard>
    </div>
  );
};

export default withPagePermission(PartnerUserList, PERMISSIONS.ADMIN_ZONE_USER_VIEW);
